<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { Action } from "@/modules/rule_engine/ruleEngineClasses";
import { describeAction } from "@/modules/rule_engine/describe";
import { useRuleEnginedStore } from "@/modules/rule_engine/store";
import RuleSegments from "@/modules/rule_engine/components/RuleSegments.vue";

const { t } = useI18n();

const emit = defineEmits(["edit-action", "delete-action"]);

const props = withDefaults(
	defineProps<{
		actions: Action[];
		editIsAllowed?: boolean;
	}>(),
	{ editIsAllowed: false }
);

const describedActions = computed(() =>
	(props.actions ?? []).map((action) => ({
		action,
		segments: describeAction(action, t, {
			actionValues: useRuleEnginedStore().ruleEngineState.values_for_action_type,
		}),
	}))
);

/** Labels carry their own colour, so they stay a chip rather than plain text. */
const labelColour = (action: Action): string | undefined => {
	if (action.type !== "add_label_to_bex_reservation") return undefined;

	const labels =
		useRuleEnginedStore().ruleEngineState.values_for_action_type
			.add_label_to_bex_reservation ?? [];

	return labels.find((label) => label.uuid === action.label_id)?.background;
};
</script>

<template>
	<div
		v-for="({ action, segments }, index) in describedActions"
		:key="action.uuid ?? index"
		class="d-flex action py-1"
	>
		<template v-if="action.type === 'add_label_to_bex_reservation'">
			<rule-segments
				:connector="index === 0 ? t('rule_engine.then') : t('rule_engine.and')"
				:segments="segments.slice(0, 1)"
			/>
			<v-chip class="ml-2" :color="labelColour(action)" label>
				{{ segments.at(-1)?.text }}
			</v-chip>
		</template>
		<rule-segments
			v-else
			:connector="index === 0 ? t('rule_engine.then') : t('rule_engine.and')"
			:segments="segments"
		/>
		<v-spacer />
		<div v-if="editIsAllowed" class="flex-shrink-0">
			<v-btn
				@click="emit('edit-action', action)"
				variant="text"
				color="#1abc9c"
				>Edit</v-btn
			>
			<v-btn
				@click="emit('delete-action', action)"
				variant="text"
				color="error"
				>Delete</v-btn
			>
		</div>
	</div>
</template>

<style scoped lang="scss">
.action {
	border-left: 5px solid #e67e22;
	padding-left: 0.75rem;
}
</style>
